import JSZip from 'jszip';
import { basename, buildImportInfo, findByBasename } from './sd-card-entries';
import { SdCardError, type SdCardEntry } from './types';
import { finalize, md5Base64, type PackProgress } from './pack-common';

// P5 / LUISA → .pldat.
// Root: info.json, configuration.json, Luisa.ldat (already serial-encrypted by
// the device — copied verbatim), plus a derived Hash.txt. Recordings become
// <date>/<seq>/<time>.edf.

const P5_EDF_RE = /^(\d{4,8})_(\d{4}-\d{2}-\d{2})_(.+)\.edf$/i;

export const packP5 = async (
  entries: SdCardEntry[],
  onProgress?: PackProgress,
): Promise<Blob> => {
  const zip = new JSZip();

  const info = findByBasename(entries, 'info.json');
  if (!info) {
    throw new SdCardError('The LUISA card is missing info.json.');
  }
  zip.file('info.json', info.file);

  const config = findByBasename(entries, 'configuration.json');
  if (config) zip.file('configuration.json', config.file);

  const ldat = entries.find((entry) =>
    basename(entry.path).toLowerCase().endsWith('.ldat'),
  );
  if (!ldat) {
    throw new SdCardError('The LUISA card is missing the statistic (.ldat) file.');
  }
  zip.file('Luisa.ldat', ldat.file);

  // pTS derives Hash.txt = base64(MD5(Luisa.ldat)) during SD-card import
  // (P5FileSystemImporter) and its P5ContainerFileImporter rejects a .pldat that
  // lacks it. Signal Viewer ignores the entry.
  zip.file('Hash.txt', md5Base64(await ldat.file.arrayBuffer()));

  const recordings = entries
    .map((entry) => {
      const match = basename(entry.path).match(P5_EDF_RE);
      return match
        ? { entry, seq: match[1], date: match[2], time: match[3] }
        : null;
    })
    .filter((rec): rec is NonNullable<typeof rec> => rec !== null);

  if (recordings.length === 0) {
    throw new SdCardError('No LUISA recordings (*.edf) were found on the card.');
  }

  for (const recording of recordings) {
    zip.file(
      `${recording.date}/${recording.seq}/${recording.time}.edf`,
      recording.entry.file,
    );
  }

  zip.file('importInfo.txt', buildImportInfo('P5'));

  return finalize(zip, onProgress);
};
